import { useMemo } from 'react';
import { Select } from 'antd';
import styles from './ClassPicker.module.css';

interface ClassPickerProps {
  value: string;
  options: string[];
  /** Classes already used elsewhere — they'll be hidden unless equal to value */
  usedClasses?: Set<string>;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}

/**
 * Strict dropdown for picking a worksheet class identifier (context class,
 * finding class, flag class). The user can only pick from the provided list.
 * Already-used classes are filtered out unless they match the current value.
 */
export function ClassPicker({
  value,
  options,
  usedClasses,
  onChange,
  placeholder = 'Pick class',
  className,
}: ClassPickerProps) {
  const selectOptions = useMemo(
    () =>
      options
        .filter((opt) => !usedClasses?.has(opt) || opt === value)
        .map((opt) => ({ value: opt, label: humanize(opt) })),
    [options, usedClasses, value]
  );

  return (
    <Select
      size="small"
      placeholder={placeholder}
      value={value || undefined}
      options={selectOptions}
      onChange={(v) => onChange(v ?? '')}
      showSearch
      optionFilterProp="label"
      popupMatchSelectWidth={260}
      className={`${styles.picker} ${className ?? ''}`}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    />
  );
}

function humanize(value: string): string {
  return value
    .toLowerCase()
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}
